import React, { useId } from 'react';
import styles from './FormField.module.css';

/**
 * Tek kaynaklı form alanı: label + input/textarea/select + hata + yardım/karakter sayacı.
 * 44px dokunma hedefi, marka odak halkası, hata durumu ve erişilebilirlik (id/aria) otomatik.
 *
 * as: input | textarea | select
 */
const FormField = ({
  as = 'input',
  label,
  value,
  onChange,
  error,
  helperText,
  charCount = false,
  maxLength,
  required = false,
  disabled = false,
  type = 'text',
  prefix,
  suffix,
  id,
  name,
  placeholder,
  rows = 4,
  options,
  children,
  className = '',
  inputClassName = '',
  ...rest
}) => {
  const autoId = useId();
  const fieldId = id || name || autoId;
  const errorId = `${fieldId}-error`;
  const helperId = `${fieldId}-helper`;
  const describedBy =
    [error ? errorId : null, helperText ? helperId : null].filter(Boolean).join(' ') || undefined;

  const controlClasses = [
    styles.control,
    error ? styles.controlError : '',
    prefix ? styles.withPrefix : '',
    suffix ? styles.withSuffix : '',
    as === 'select' ? styles.select : '',
    inputClassName,
  ]
    .filter(Boolean)
    .join(' ');

  const commonProps = {
    id: fieldId,
    name,
    value,
    onChange,
    disabled,
    maxLength,
    'aria-required': required || undefined,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': describedBy,
    className: controlClasses,
    ...rest,
  };

  return (
    <div className={`${styles.field} ${disabled ? styles.disabled : ''} ${className}`}>
      {label && (
        <label htmlFor={fieldId} className={styles.label}>
          {label}
          {required && <span className={styles.req} aria-hidden="true"> *</span>}
        </label>
      )}

      <div className={styles.controlWrap}>
        {prefix && <span className={styles.prefix}>{prefix}</span>}
        {as === 'textarea' ? (
          <textarea {...commonProps} rows={rows} placeholder={placeholder} />
        ) : as === 'select' ? (
          <select {...commonProps}>
            {options
              ? options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))
              : children}
          </select>
        ) : (
          <input {...commonProps} type={type} placeholder={placeholder} />
        )}
        {suffix && <span className={styles.suffix}>{suffix}</span>}
      </div>

      {(error || helperText || (charCount && maxLength != null)) && (
        <div className={styles.meta}>
          {error ? (
            <span id={errorId} role="alert" className={styles.error}>
              {error}
            </span>
          ) : helperText ? (
            <span id={helperId} className={styles.helper}>
              {helperText}
            </span>
          ) : (
            <span />
          )}
          {charCount && maxLength != null && (
            <span className={styles.charCount}>
              {(value?.length ?? 0)}/{maxLength}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default FormField;
